import React, { useEffect, useRef, useState } from 'react';
import InvoiceLink from './InvoiceLink';
import { Package, MapPin, Crosshair, Search } from 'lucide-react';
import apiService from '../apiService';

/**
 * Colis web — envoi (départ/arrivée avec recherche de lieu via le proxy Places,
 * estimation serveur), historique, suivi (codes côté expéditeur, annulation en
 * recherche). Paiements : espèces au retrait ou solde Mbiyo (débité à la
 * livraison — même règle que le natif).
 */
const hav = (a, b) => {
  const R = 6371, dLa = (b.lat - a.lat) * Math.PI / 180, dLo = (b.lng - a.lng) * Math.PI / 180;
  const x = Math.sin(dLa / 2) ** 2 + Math.cos(a.lat * Math.PI / 180) * Math.cos(b.lat * Math.PI / 180) * Math.sin(dLo / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
};

function PlaceInput({ label, value, onText, onPick, bias }) {
  const [results, setResults] = useState([]);
  const timer = useRef(null);
  const search = (q) => {
    onText(q); clearTimeout(timer.current);
    if (q.trim().length < 3) { setResults([]); return; }
    timer.current = setTimeout(async () => {
      const qs = new URLSearchParams({ q: q.trim() });
      if (bias) { qs.set('lat', bias.lat); qs.set('lng', bias.lng); }
      const r = await apiService.get(`/places/autocomplete?${qs}`).catch(() => null);
      setResults(r?.results || []);
    }, 300);
  };
  const pick = async (p) => {
    setResults([]); onText(p.description || p.mainText);
    const r = await apiService.get(`/places/details?place_id=${encodeURIComponent(p.placeId)}`).catch(() => null);
    if (r?.success && r.location) onPick({ lat: r.location.latitude, lng: r.location.longitude });
  };
  return (
    <div className="mk-field" style={{ position: 'relative' }}>
      <label>{label}</label>
      <div style={{ position: 'relative' }}>
        <Search size={15} style={{ position: 'absolute', left: 11, top: 13, color: 'var(--mb-text-3)' }} />
        <input style={{ paddingLeft: 32 }} value={value} onChange={e => search(e.target.value)} placeholder="Adresse, repère, avenue…" />
      </div>
      {results.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 30, background: '#fff', border: '1px solid var(--mb-border)', borderRadius: 10, boxShadow: 'var(--mb-shadow-float)', maxHeight: 220, overflowY: 'auto' }}>
          {results.map(p => (
            <div key={p.placeId} onClick={() => pick(p)} style={{ padding: '9px 12px', cursor: 'pointer', borderBottom: '1px solid var(--mb-gray-100)' }}>
              <div style={{ fontSize: '.88rem', fontWeight: 600 }}>{p.mainText}</div>
              {p.secondaryText && <div style={{ fontSize: '.74rem', color: 'var(--mb-text-3)' }}>{p.secondaryText}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function ParcelSend({ nav }) {
  const [pickupText, setPickupText] = useState('');
  const [pickup, setPickup] = useState(null);
  const [dropText, setDropText] = useState('');
  const [drop, setDrop] = useState(null);
  const [desc, setDesc] = useState('');
  const [rName, setRName] = useState('');
  const [rPhone, setRPhone] = useState('');
  const [pay, setPay] = useState('cash');
  const [est, setEst] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const locate = () => {
    navigator.geolocation?.getCurrentPosition(
      p => { setPickup({ lat: p.coords.latitude, lng: p.coords.longitude }); if (!pickupText) setPickupText('Ma position actuelle'); },
      () => {}, { enableHighAccuracy: true, timeout: 8000 }
    );
  };
  useEffect(() => { locate(); }, []); // eslint-disable-line

  const distanceKm = pickup && drop ? Math.round(hav(pickup, drop) * 10) / 10 : null;

  useEffect(() => {
    if (!distanceKm) { setEst(null); return; }
    const qs = new URLSearchParams({
      distance_km: distanceKm, payment_method: pay,
      pickup_lat: pickup.lat, pickup_lng: pickup.lng, dropoff_lat: drop.lat, dropoff_lng: drop.lng,
    });
    apiService.get(`/parcels/estimate?${qs}`).then(r => setEst(r?.success ? r : null)).catch(() => setEst(null));
  }, [distanceKm, pay]); // eslint-disable-line

  const submit = async () => {
    setError(null);
    if (!pickup || !drop) return setError('Indiquez le point de retrait et le point de livraison.');
    if (!rPhone.trim()) return setError('Le numéro du destinataire est requis.');
    setBusy(true);
    try {
      const res = await apiService.post('/parcels', {
        pickup_address: pickupText, pickup_lat: pickup.lat, pickup_lng: pickup.lng,
        dropoff_address: dropText, dropoff_lat: drop.lat, dropoff_lng: drop.lng,
        distance_km: distanceKm, parcel_description: desc.trim(),
        recipient_name: rName.trim(), recipient_phone: rPhone.trim(), payment_method: pay,
      });
      if (!res?.success || !res.parcel) throw new Error(res?.error || 'Envoi impossible');
      nav({ page: 'parcel', parcelId: res.parcel.parcel_id });
    } catch (e) { setError(e?.error || e?.message || 'Erreur réseau'); }
    finally { setBusy(false); }
  };

  return (
    <div className="mk-container">
      <div className="mk-two">
        <div className="mk-panel">
          <h3>📦 Envoyer un colis</h3>
          <PlaceInput label="Point de retrait *" value={pickupText} onText={setPickupText} onPick={setPickup} bias={pickup} />
          <button className="mk-btn ghost" style={{ marginBottom: 10 }} onClick={locate}><Crosshair size={15} /> {pickup ? 'Position de retrait détectée ✓' : 'Utiliser ma position'}</button>
          <PlaceInput label="Point de livraison *" value={dropText} onText={setDropText} onPick={setDrop} bias={pickup} />
          <div className="mk-field"><label>Description du colis</label>
            <input value={desc} onChange={e => setDesc(e.target.value)} placeholder="Ex. documents, petit carton…" maxLength={300} /></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div className="mk-field"><label>Destinataire</label>
              <input value={rName} onChange={e => setRName(e.target.value)} placeholder="Nom" /></div>
            <div className="mk-field"><label>Téléphone destinataire *</label>
              <input value={rPhone} onChange={e => setRPhone(e.target.value)} placeholder="0991234567" inputMode="tel" /></div>
          </div>
          <div className="mk-pay">
            <button className={pay === 'cash' ? 'on' : ''} onClick={() => setPay('cash')}>💵 Espèces au retrait</button>
            <button className={pay === 'balance' ? 'on' : ''} onClick={() => setPay('balance')}>👛 Solde Mbiyo</button>
          </div>
          <p className="mk-note" style={{ marginTop: 8 }}>Avec le solde Mbiyo, le montant n'est débité qu'à la livraison confirmée.</p>
        </div>

        <div className="mk-panel">
          <h3>Estimation</h3>
          <div className="mk-total"><span>Distance</span><span>{distanceKm != null ? `${distanceKm} km` : '—'}</span></div>
          <div className="mk-total grand"><span>Frais de course</span><span>{est ? `${(est.delivery_fee || est.fee || 0).toLocaleString('fr-FR')} FC` : '—'}</span></div>
          {error && <p className="mk-note err" style={{ marginTop: 8 }}>{error}</p>}
          <button className="mk-btn primary block" style={{ marginTop: 12 }} disabled={busy || !est} onClick={submit}>
            {busy ? 'Envoi…' : 'Trouver un livreur'}
          </button>
          <button className="mk-btn ghost block" style={{ marginTop: 8 }} onClick={() => nav({ page: 'parcels' })}>Mes colis</button>
        </div>
      </div>
    </div>
  );
}

const PSTATUS = {
  searching: ['En recherche de livreur', '#FFB900'], accepted: ['Livreur en route vers le retrait', '#FF7800'],
  picked_up: ['Colis récupéré', '#FF5500'], delivering: ['En route vers le destinataire', '#F54119'],
  delivered: ['Livré ✓', '#10B981'], cancelled: ['Annulé', '#DC2626'], no_driver: ['Aucun livreur trouvé', '#DC2626'],
};

export function ParcelHistory({ nav }) {
  const [parcels, setParcels] = useState(null);
  useEffect(() => {
    apiService.get('/parcels/client/history').then(r => setParcels(r?.parcels || [])).catch(() => setParcels([]));
  }, []);
  if (parcels === null) return <div className="mk-center"><div className="mk-spin" /> Chargement…</div>;
  return (
    <div className="mk-container">
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
        <button className="mk-btn primary" onClick={() => nav({ page: 'parcel-send' })}><Package size={16} /> Envoyer un colis</button>
      </div>
      <div className="mk-panel" style={{ marginTop: 12 }}>
        <h3>Mes colis</h3>
        {!parcels.length && <div className="mk-center">Aucun colis envoyé pour le moment.</div>}
        {parcels.map(p => {
          const [lbl, color] = PSTATUS[p.status] || [p.status, '#5C636E'];
          return (
            <div className="mk-line" key={p.parcel_id} style={{ cursor: 'pointer' }} onClick={() => nav({ page: 'parcel', parcelId: p.parcel_id })}>
              <div className="info">
                <div className="name">{p.pickup?.address?.slice(0, 34)} → {p.dropoff?.address?.slice(0, 34)}</div>
                <div className="opt">{new Date(p.created_at).toLocaleString('fr-FR')} · {(p.delivery_fee || 0).toLocaleString('fr-FR')} FC</div>
              </div>
              <span className="mk-open" style={{ background: `${color}1c`, color }}>{lbl}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function ParcelDetail({ parcelId, nav }) {
  const [parcel, setParcel] = useState(null);
  const timer = useRef(null);

  const load = () => apiService.get(`/parcels/${parcelId}`).then(r => { if (r?.success) setParcel(r.parcel); }).catch(() => {});
  useEffect(() => {
    load();
    timer.current = setInterval(() => {
      setParcel(prev => {
        if (prev && ['delivered', 'cancelled', 'no_driver'].includes(prev.status)) { clearInterval(timer.current); return prev; }
        load(); return prev;
      });
    }, 10000);
    return () => clearInterval(timer.current);
  }, [parcelId]); // eslint-disable-line

  const cancel = async () => {
    if (!window.confirm('Annuler ce colis ?')) return;
    const r = await apiService.patch(`/parcels/${parcelId}/cancel`, {}).catch(e => e);
    if (!r?.success) alert(r?.error || r?.message || 'Annulation impossible');
    load();
  };

  if (!parcel) return <div className="mk-center"><div className="mk-spin" /> Chargement…</div>;
  const [lbl, color] = PSTATUS[parcel.status] || [parcel.status, '#5C636E'];
  const isSender = parcel.viewer_role === 'sender';

  return (
    <div className="mk-container">
      <div className="mk-two">
        <div className="mk-panel">
          <h3>📦 Colis <span className="mk-open" style={{ background: `${color}1c`, color, marginLeft: 8 }}>{lbl}</span></h3>
          <p className="mk-note"><MapPin size={13} /> Retrait : {parcel.pickup?.address}<br /><MapPin size={13} /> Livraison : {parcel.dropoff?.address}</p>
          {parcel.parcel_description && <p className="mk-desc">Contenu : {parcel.parcel_description}</p>}
          <div className="mk-total"><span>Destinataire</span><span>{parcel.recipient_name || '—'} · {parcel.recipient_phone}</span></div>
          <div className="mk-total grand"><span>Frais</span><span>{(parcel.delivery_fee || 0).toLocaleString('fr-FR')} FC</span></div>
          {isSender && <InvoiceLink type="parcel" id={parcel.parcel_id} compact />}
          {parcel.status === 'searching' && (
            <button className="mk-btn ghost" style={{ color: 'var(--mb-danger)', marginTop: 10 }} onClick={cancel}>Annuler l'envoi</button>
          )}
          <button className="mk-btn ghost block" style={{ marginTop: 8 }} onClick={() => nav({ page: 'parcels' })}>← Mes colis</button>
        </div>

        <div>
          {parcel.driver && (
            <div className="mk-panel">
              <h3>🛵 Livreur</h3>
              <div className="mk-line">
                <div className="info"><div className="name">{parcel.driver.name}</div><div className="opt">{parcel.driver.vehicle_plate || ''}</div></div>
                <button className="mk-iconbtn" onClick={() => window.open(`tel:${parcel.driver.phone}`)}>📞</button>
              </div>
            </div>
          )}
          {isSender && parcel.pickup_code && ['accepted'].includes(parcel.status) && (
            <div className="mk-code" style={{ marginTop: parcel.driver ? 14 : 0 }}>
              <b>{parcel.pickup_code}</b>
              <p>Code de RETRAIT — donnez-le au livreur quand il récupère le colis.</p>
            </div>
          )}
          {parcel.delivery_code && (
            <div className="mk-code" style={{ marginTop: 14 }}>
              <b>{parcel.delivery_code}</b>
              <p>Code de LIVRAISON — à communiquer au destinataire : il le donnera au livreur à la réception.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
